var _dec, _class, _ColorPreset, _IconAddLine;
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { Component } from 'react';
import { withStyle } from '@instructure/emotion';
import { omitProps } from '@instructure/ui-react-utils';
import conversions from '@instructure/ui-color-utils';
import { IconButton, Button } from '@instructure/ui-buttons';
import { View } from '@instructure/ui-view';
import { Tooltip } from '@instructure/ui-tooltip';
import { Popover } from '@instructure/ui-popover';
import { Text } from '@instructure/ui-text';
import { Drilldown } from '@instructure/ui-drilldown';
import { IconAddLine, IconCheckDarkSolid } from '@instructure/ui-icons';
import { ColorIndicator } from "../ColorIndicator/index.js";
import { ColorMixer } from "../ColorMixer/index.js";
import { ColorContrast } from "../ColorContrast/index.js";
import generateStyle from "./styles.js";
import generateComponentTheme from "./theme.js";
import { allowedProps } from "./props.js";
/**
---
category: components
---
**/
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
let ColorPreset = (_dec = withStyle(generateStyle, generateComponentTheme), _dec(_class = (_ColorPreset = class ColorPreset extends Component {
  constructor(props) {
    super(props);
    this.ref = null;
    this.handleRef = el => {
      const elementRef = this.props.elementRef;
      this.ref = el;
      if (typeof elementRef === 'function') {
        elementRef(el);
      }
    };
    this.onMenuItemSelected = color => (_e, args) => {
      if (args.value === 'select') {
        this.props.onSelect(color);
      }
      if (args.value === 'remove') {
        var _this$props, _this$props$colorMixe;
        (_this$props = this.props) === null || _this$props === void 0 ? void 0 : (_this$props$colorMixe = _this$props.colorMixerSettings) === null || _this$props$colorMixe === void 0 ? void 0 : _this$props$colorMixe.onPresetChange(this.props.colors.filter(clr => clr !== color));
      }
    };
    this.renderAddNewPresetButton = () => {
      var _this$props2, _this$props2$styles, _this$props$styles, _this$props3, _this$props3$colorMix, _this$props3$colorMix2, _this$props4, _this$props4$colorMix, _this$props$styles2, _this$props$styles3, _this$props$colorMixe2, _this$props$colorMixe3;
      return _jsxs(Popover, {
        renderTrigger: _jsx("div", {
          css: (_this$props2 = this.props) === null || _this$props2 === void 0 ? void 0 : (_this$props2$styles = _this$props2.styles) === null || _this$props2$styles === void 0 ? void 0 : _this$props2$styles.addNewPresetButton,
          children: _jsx(IconButton, {
            disabled: this.props.disabled,
            screenReaderLabel: this.props.colorMixerSettings.addNewPresetButtonScreenReaderLabel,
            children: _IconAddLine || (_IconAddLine = _jsx(IconAddLine, {}))
          })
        }),
        isShowingContent: this.state.openAddNew,
        onShowContent: () => {
          if (this.props.disabled) return;
          this.setState({
            openAddNew: true
          });
        },
        onHideContent: () => {
          this.setState({
            openAddNew: false
          });
        },
        on: "click",
        screenReaderLabel: this.props.popoverScreenReaderLabel,
        shouldContainFocus: true,
        shouldReturnFocus: true,
        shouldCloseOnDocumentClick: true,
        offsetY: 16,
        mountNode: () => document.getElementById('main'),
        children: [_jsxs("div", {
          css: (_this$props$styles = this.props.styles) === null || _this$props$styles === void 0 ? void 0 : _this$props$styles.popoverContent,
          children: [_jsx(ColorMixer, {
            value: conversions.colorToHex8(this.state.newColor),
            onChange: newColor => this.setState({
              newColor: conversions.colorToRGB(newColor)
            }),
            withAlpha: (_this$props3 = this.props) === null || _this$props3 === void 0 ? void 0 : (_this$props3$colorMix = _this$props3.colorMixerSettings) === null || _this$props3$colorMix === void 0 ? void 0 : (_this$props3$colorMix2 = _this$props3$colorMix.colorMixer) === null || _this$props3$colorMix2 === void 0 ? void 0 : _this$props3$colorMix2.withAlpha,
            rgbRedInputScreenReaderLabel: this.props.colorMixerSettings.colorMixer.rgbRedInputScreenReaderLabel,
            rgbGreenInputScreenReaderLabel: this.props.colorMixerSettings.colorMixer.rgbGreenInputScreenReaderLabel,
            rgbBlueInputScreenReaderLabel: this.props.colorMixerSettings.colorMixer.rgbBlueInputScreenReaderLabel,
            rgbAlphaInputScreenReaderLabel: this.props.colorMixerSettings.colorMixer.rgbAlphaInputScreenReaderLabel,
            colorSliderNavigationExplanationScreenReaderLabel: this.props.colorMixerSettings.colorMixer.colorSliderNavigationExplanationScreenReaderLabel,
            alphaSliderNavigationExplanationScreenReaderLabel: this.props.colorMixerSettings.colorMixer.alphaSliderNavigationExplanationScreenReaderLabel,
            colorPaletteNavigationExplanationScreenReaderLabel: this.props.colorMixerSettings.colorMixer.colorPaletteNavigationExplanationScreenReaderLabel
          }), ((_this$props4 = this.props) === null || _this$props4 === void 0 ? void 0 : (_this$props4$colorMix = _this$props4.colorMixerSettings) === null || _this$props4$colorMix === void 0 ? void 0 : _this$props4$colorMix.colorContrast) && _jsx("div", {
            css: (_this$props$styles2 = this.props.styles) === null || _this$props$styles2 === void 0 ? void 0 : _this$props$styles2.popoverContrastBlock,
            children: _jsx(ColorContrast, {
              firstColor: this.props.colorMixerSettings.colorContrast.firstColor,
              secondColor: conversions.colorToHex8(this.state.newColor),
              label: this.props.colorMixerSettings.colorContrast.label,
              successLabel: this.props.colorMixerSettings.colorContrast.successLabel,
              failureLabel: this.props.colorMixerSettings.colorContrast.failureLabel,
              normalTextLabel: this.props.colorMixerSettings.colorContrast.normalTextLabel,
              largeTextLabel: this.props.colorMixerSettings.colorContrast.largeTextLabel,
              graphicsTextLabel: this.props.colorMixerSettings.colorContrast.graphicsTextLabel,
              firstColorLabel: this.props.colorMixerSettings.colorContrast.firstColorLabel,
              secondColorLabel: this.props.colorMixerSettings.colorContrast.secondColorLabel
            })
          })]
        }), _jsxs("div", {
          css: (_this$props$styles3 = this.props.styles) === null || _this$props$styles3 === void 0 ? void 0 : _this$props$styles3.popoverFooter,
          children: [_jsx(Button, {
            onClick: () => {
              var _this$props5, _this$props5$colorMix;
              (_this$props5 = this.props) === null || _this$props5 === void 0 ? void 0 : (_this$props5$colorMix = _this$props5.colorMixerSettings) === null || _this$props5$colorMix === void 0 ? void 0 : _this$props5$colorMix.onPresetChange([conversions.colorToHex8(this.state.newColor), ...this.props.colors]);
              this.setState({
                openAddNew: false
              });
            },
            color: "primary",
            margin: "0 xx-small 0 xx-small",
            children: (_this$props$colorMixe2 = this.props.colorMixerSettings) === null || _this$props$colorMixe2 === void 0 ? void 0 : _this$props$colorMixe2.popoverAddButtonLabel
          }), _jsx(Button, {
            onClick: () => this.setState({
              openAddNew: false
            }),
            color: "secondary",
            margin: "0 xx-small 0 xx-small",
            children: (_this$props$colorMixe3 = this.props.colorMixerSettings) === null || _this$props$colorMixe3 === void 0 ? void 0 : _this$props$colorMixe3.popoverCloseButtonLabel
          })]
        })]
      });
    };
    this.renderColorIndicator = (color, selectOnClick) => {
      const indicatorBase = this.renderIndicatorBase(color, selectOnClick);
      return this.props.disabled ? indicatorBase : this.renderIndicatorTooltip(indicatorBase, color);
    };
    this.renderIndicatorBase = (color, selectOnClick) => {
      var _this$props6, _this$props6$styles;
      const hexCode = color;
      const isSelected = this.isSelectedColor(color);
      const screenReaderLabel = typeof this.props.colorScreenReaderLabel === 'function' ? this.props.colorScreenReaderLabel(hexCode, isSelected) : hexCode;
      return _jsx(View, {
        disabled: this.props.disabled,
        position: "relative",
        width: "2.375rem",
        height: "2.375rem",
        background: "transparent",
        margin: "xx-small",
        display: "inline-block",
        borderRadius: "medium",
        borderWidth: "0",
        padding: "0",
        cursor: this.props.disabled ? 'not-allowed' : 'auto',
        as: "button",
        type: "button",
        "aria-label": screenReaderLabel,
        ...(selectOnClick ? {
          onClick: () => this.props.onSelect(color)
        } : {}),
        children: _jsxs("div", {
          children: [_jsx(ColorIndicator, {
            color: color,
            shape: "rectangle",
            role: "presentation"
          }), this.isSelectedColor(color) && _jsx("div", {
            css: (_this$props6 = this.props) === null || _this$props6 === void 0 ? void 0 : (_this$props6$styles = _this$props6.styles) === null || _this$props6$styles === void 0 ? void 0 : _this$props6$styles.selectedIndicator,
            children: _jsx(IconCheckDarkSolid, {
              themeOverride: {
                sizeXSmall: '0.8rem'
              },
              size: "x-small"
            })
          })]
        })
      });
    };
    this.renderIndicatorTooltip = (child, color) => {
      return _jsx(Tooltip, {
        renderTip: _jsx("div", {
          children: color
        }),
        elementRef: element => {
          if (element && element.firstElementChild instanceof HTMLButtonElement) {
            // The tooltip and the button's aria-label has the same text content. This is redundant for screenreaders.
            // Aria-describedby is removed to bypass reading the tooltip twice
            element.firstElementChild.removeAttribute('aria-describedby');
          }
        },
        children: child
      });
    };
    this.renderSettingsMenu = (color, index) => _jsx(Drilldown, {
      onSelect: this.onMenuItemSelected(color),
      trigger: this.renderColorIndicator(color),
      rootPageId: "root",
      width: "10rem",
      offsetY: "15rem",
      children: _jsxs(Drilldown.Page, {
        withoutHeaderSeparator: true,
        id: "root",
        renderTitle: color,
        children: [_jsx(Drilldown.Option, {
          value: "select",
          id: "select",
          children: this.props.colorMixerSettings.selectColorLabel
        }), _jsx(Drilldown.Option, {
          value: "remove",
          id: "remove",
          children: this.props.colorMixerSettings.removeColorLabel
        })]
      })
    }, `color-preset-color-${index}`);
    this.state = {
      openEditor: false,
      openAddNew: false,
      newColor: {
        r: 51,
        g: 99,
        b: 42,
        a: 1
      }
    };
  }
  componentDidMount() {
    var _this$props$makeStyle, _this$props7;
    (_this$props$makeStyle = (_this$props7 = this.props).makeStyles) === null || _this$props$makeStyle === void 0 ? void 0 : _this$props$makeStyle.call(_this$props7);
  }
  componentDidUpdate() {
    var _this$props$makeStyle2, _this$props8;
    (_this$props$makeStyle2 = (_this$props8 = this.props).makeStyles) === null || _this$props$makeStyle2 === void 0 ? void 0 : _this$props$makeStyle2.call(_this$props8);
  }
  get isModifiable() {
    var _this$props$colorMixe4;
    return typeof ((_this$props$colorMixe4 = this.props.colorMixerSettings) === null || _this$props$colorMixe4 === void 0 ? void 0 : _this$props$colorMixe4.onPresetChange) === 'function';
  }
  isSelectedColor(color) {
    const selected = this.props.selected;
    return !!selected && conversions.colorToHex8(selected) === conversions.colorToHex8(color);
  }
  render() {
    const _this$props9 = this.props,
      styles = _this$props9.styles,
      label = _this$props9.label,
      colors = _this$props9.colors;
    if (!this.isModifiable && colors.length === 0) {
      // if there is no preset and no ability to add new,
      // there is no point of rendering the component
      return null;
    }
    return _jsxs("div", {
      ...omitProps(this.props, ColorPreset.allowedProps),
      ref: this.handleRef,
      css: styles === null || styles === void 0 ? void 0 : styles.colorPreset,
      "data-cid": "ColorPreset",
      children: [label && _jsx("div", {
        css: styles === null || styles === void 0 ? void 0 : styles.label,
        children: _jsx(Text, {
          weight: "bold",
          children: label
        })
      }), this.isModifiable && this.renderAddNewPresetButton(), colors.map((color, index) => {
        return this.isModifiable ? this.renderSettingsMenu(color, index) : _jsx("div", {
          children: this.renderColorIndicator(color, true)
        }, `color-preset-color-${index}`);
      })]
    });
  }
}, _ColorPreset.displayName = "ColorPreset", _ColorPreset.allowedProps = allowedProps, _ColorPreset.componentId = 'ColorPreset', _ColorPreset.defaultProps = {
  disabled: false
}, _ColorPreset)) || _class);
export { ColorPreset };
export default ColorPreset;